import endOfMonth from 'date-fns/endOfMonth';
import format from 'date-fns/format';
import formatDistanceStrict from 'date-fns/formatDistanceStrict';
import isMatch from 'date-fns/isMatch';
import parse from 'date-fns/parse';
import parseISO from 'date-fns/parseISO';
import startOfMonth from 'date-fns/startOfMonth';
import startOfYear from 'date-fns/startOfYear';
import subDays from 'date-fns/subDays';
import subMonths from 'date-fns/subMonths';
import subYears from 'date-fns/subYears';
import TimeTypeEnum from '@/constants/contributions/TimeTypeEnum';
import DateType from '@/constants/DateType';
import {
  API_DATE_FORMAT,
  API_TIME_FORMAT,
  DEFAULT_DATE_FORMAT,
  HOURS_IN_DAY,
  MINUTES_IN_HOUR,
} from '@/lib/constants/common';
import { BEGINNING_OF_TIME } from '@/lib/constants/dateConstants';
import { roundToFixed } from './numberUtils';

/**
 * Takes a datestring and returns a date object. Addresses inconsistencies in
 * how we receieve date strings. Sometimes we get full ISO formatted strings,
 * sometimes we get only YYYY-MM-DD. Simply creating a `new Date()` with the
 * latter format introduces timezone issues, so we need to manually create the
 * date with the noon local timestamp.
 * @param {string} dateStr
 * @return {Date}
 */
const dateStringToDate = (dateStr) =>
  new Date(`${dateStr.replace(/-/g, '/').slice(0, 10)} 12:00`);

const formatDistance = (
  date,
  baseDate = new Date(Date.now()),
  props = { addSuffix: true, unit: 'day' },
) => {
  const newDate = dateStringToDate(date);
  const formattedDistance = date
    ? formatDistanceStrict(newDate, baseDate, props)
    : '';
  return formattedDistance;
};

const formatDate = (date, dateFormat = DEFAULT_DATE_FORMAT) => {
  try {
    return format(parseISO(date), dateFormat);
  } catch (e) {
    return date;
  }
};

const formatDateObject = (date, dateFormat = DEFAULT_DATE_FORMAT) => {
  try {
    return format(date, dateFormat);
  } catch (e) {
    return date;
  }
};

const formatDateToString = (date) => {
  let d = date;

  if (typeof d === 'string') {
    d = new Date(d);
  }

  return format(d, API_DATE_FORMAT);
};

const formatTimeToString = (date) => {
  let d = date;

  if (typeof d === 'string') {
    d = new Date(d);
  }

  return format(d, API_TIME_FORMAT);
};

const isValidDate = (date, dateFormat = DEFAULT_DATE_FORMAT) =>
  isMatch(date, dateFormat);

const getTimeInHours = (value, type = TimeTypeEnum.HOURS) => {
  if (type === TimeTypeEnum.MINUTES) {
    return roundToFixed(value / MINUTES_IN_HOUR);
  }
  if (type === TimeTypeEnum.DAYS) {
    return value * HOURS_IN_DAY;
  }

  return Number(value);
};

const parseDateValue = (date) => {
  return date ? parse(date, API_DATE_FORMAT, new Date()) : new Date();
};

const setDateForFilters = (form) => {
  return {
    ...form,
    endDate: form.endDate ? formatDateToString(form.endDate) : null,
    startDate: form.startDate ? formatDateToString(form.startDate) : null,
  };
};

const convertMonthNumberToText = (number) =>
  new Date(0, number - 1).toLocaleString('en', { month: 'long' });

const convertDateTypeToDates = (type) => {
  let start;
  let end;
  const today = new Date(Date.now());

  today.setHours(0);
  today.setMinutes(0);
  today.setSeconds(0);
  today.setMilliseconds(0);

  switch (type) {
    case DateType.CALENDAR_YTD:
      start = startOfYear(today);
      end = today;
      break;
    case DateType.ALL_TIME:
      start = BEGINNING_OF_TIME;
      end = today;
      break;
    case DateType.LAST_MONTH:
      end = endOfMonth(subMonths(today, 1));
      start = startOfMonth(end);
      break;
    case DateType.LAST_30_DAYS:
      start = subDays(today, 30);
      end = today;
      break;
    case DateType.LAST_6_MONTHS:
      start = subDays(today, 180);
      end = today;
      break;
    case DateType.LAST_12_MONTHS:
    default:
      start = subYears(today, 1);
      end = today;
      break;
  }

  return [start, end];
};

const setDatesFromDateType = (obj) => {
  const { dateType } = obj;

  if (!dateType || dateType === DateType.CUSTOM_DATES) {
    return obj;
  }

  const [startDate, endDate] = convertDateTypeToDates(dateType);

  return {
    ...obj,
    startDate: formatDateToString(startDate),
    endDate: formatDateToString(endDate),
  };
};

export {
  convertDateTypeToDates,
  convertMonthNumberToText,
  dateStringToDate,
  formatDate,
  formatDateObject,
  formatDateToString,
  formatDistance,
  formatTimeToString,
  getTimeInHours,
  isValidDate,
  parseDateValue,
  setDateForFilters,
  setDatesFromDateType,
};
